const buildPath = (...elements: string[]) => `/${elements.join('/')}`;

export const SCOPES = {
  globals: 'globals',
  accounts: 'accounts',
  stripe: 'stripe',
  rw_members: 'rw_member',
  ambassadors: 'ambassadors',
  employers: 'employers',
  workers: 'workers',
  worker_job_positions: 'worker_job_positions',
  restaurants: buildPath('employers', 'restaurants').slice(1),
  job_positions: buildPath('employers', 'job_positions').slice(1),
  dashboard: 'dashboard',
  form: 'form',
  taxonomy: 'taxonomy',
  cv_parsing: 'cv_parsing',
  graphql: 'graphql',
  social_auth: 'auth',
  organizations: 'organizations',
  companies: buildPath('organizations', 'companies').slice(1),
  klaaryo: 'klaaryo',
  surveys: 'surveys',
};

type GenericEndpoint = { [key: string]: string };

type SurveysEndpoints = {
  root: string;
  pending: string;
  response: (surveyKey: string) => string;
};

type AmbassadorsEndpoints = {
  root: string;
  me: string;
  metrics: string;
  challenges: string;
  acceptTermsAndConditions: string;
  jobPositionPerformanceMetrics: string;
  managed: string;
  managedAggregate: string;
  managedRoster: string;
  managedMetrics: (ambassadorCode: string) => string;
  managedJobPositionPerformanceMetrics: (ambassadorCode: string) => string;
};

interface EndpointType {
  globals: GenericEndpoint;
  accounts: GenericEndpoint;
  rwMembers: GenericEndpoint;
  ambassadors: AmbassadorsEndpoints;
  employers: GenericEndpoint;
  restaurants: GenericEndpoint;
  jobPositions: GenericEndpoint;
  workers: GenericEndpoint;
  workerJobPositions: GenericEndpoint;
  dashboard: GenericEndpoint;
  taxonomy: GenericEndpoint;
  cvParsing: GenericEndpoint;
  socialAuth: GenericEndpoint;
  organizations: GenericEndpoint;
  companies: GenericEndpoint;
  klaaryo: GenericEndpoint;
  surveys: SurveysEndpoints;
}

export const ENDPOINTS: EndpointType = {
  globals: {
    root: SCOPES.globals,
    variables: buildPath(SCOPES.globals, 'variables'),
    status: buildPath(SCOPES.globals, 'status'),
    restworldRating: buildPath(SCOPES.globals, 'rating'),
    compressImage: buildPath(SCOPES.globals, 'compress_image'),
    shortenUrl: '/shorten_url',
    unsubscribedEmails: buildPath(SCOPES.globals, 'unsubscribed_emails'),
  },
  accounts: {
    root: SCOPES.accounts,
    login: buildPath(SCOPES.accounts, 'login'),
    signUp: buildPath(SCOPES.accounts, 'sign_up'),
    formId: buildPath(SCOPES.form),
    formSessionId: buildPath(SCOPES.form, 'form_session'),
    forgotPassword: buildPath(SCOPES.accounts, 'forgot_password'),
    resetPassword: buildPath(SCOPES.accounts, 'reset_password'),
    passwordlessLogin: buildPath(SCOPES.accounts, 'get_passwordless_login'),
    me: buildPath(SCOPES.accounts, 'me'),
    marketingConsent: buildPath(SCOPES.accounts, 'marketing_consent'),
    setupCheckoutSession: buildPath(SCOPES.stripe, 'setup_checkout_session'),
    getIntercomContactId: buildPath(SCOPES.accounts, 'intercom_contact_id'),
    phoneOtpVerification: buildPath(SCOPES.accounts, 'verification_phone_otp'),
    verifyPhoneOtp: buildPath(SCOPES.accounts, 'verify_phone_otp'),
    updateAuthenticationEmail: buildPath(SCOPES.accounts, 'update_user_authentication'),
    verifyEmail: buildPath(SCOPES.accounts, 'verify_email'),
    logout: buildPath(SCOPES.accounts, 'logout'),
    delete: buildPath(SCOPES.accounts),
    restaurant_exploration_popup_details: buildPath(SCOPES.accounts, 'restaurant_exploration_popup_details'),
    flexibleSlots: buildPath(SCOPES.accounts, 'flexible_slots'),
    activateFlexibleSlot: buildPath(SCOPES.accounts, 'flexible_slots', 'activate'),
    cancelFlexibleSlot: buildPath(SCOPES.accounts, 'flexible_slots', 'cancel'),
    flexibleSlotPaymentSession: buildPath(SCOPES.accounts, 'flexible_slots', 'start_payment_session'),
    freeSlot: buildPath(SCOPES.accounts, 'flexible_slots', 'free_slot'),
    employerUserContact: buildPath(SCOPES.accounts, 'employer_user_contact'),
    employerUserContactAccessLink: buildPath(SCOPES.accounts, 'employer_user_contact', 'access_link'),
    userAccessLink: buildPath(SCOPES.accounts, 'access_link'),
    secondaryContacts: buildPath(SCOPES.accounts, 'secondary_contacts'),
    checkUserExistence: buildPath(SCOPES.accounts, 'check_user_existence'),
    cancelSlots: buildPath(SCOPES.accounts, 'cancel_slots'),
    annualSubscription: buildPath(SCOPES.accounts, 'subscriptions', 'annual'),
    nextPaymentDetails: buildPath(SCOPES.accounts, 'invoices', 'next_payment_details'),
    consumptionDetails: buildPath(SCOPES.accounts, 'invoices', 'consumption_details'),
  },
  rwMembers: {
    root: SCOPES.rw_members,
    wallet: buildPath(SCOPES.rw_members, 'wallet', 'csm'),
    activeApplications: buildPath(SCOPES.rw_members, 'active_job_applications'),
    agenda: buildPath(SCOPES.rw_members, 'agenda', 'csm'),
    associatedTasks: buildPath(SCOPES.rw_members, 'tasks', 'csm'),
    workload: buildPath(SCOPES.rw_members, 'workload'),
    alertJpsWithLowShortlisted: buildPath(SCOPES.rw_members, 'alert_jps_with_low_shortlisted'),
    alerts: buildPath(SCOPES.rw_members, 'alerts'),
  },
  ambassadors: {
    root: SCOPES.ambassadors,
    me: buildPath(SCOPES.ambassadors, 'me'),
    metrics: buildPath(SCOPES.ambassadors, 'metrics'),
    challenges: buildPath(SCOPES.ambassadors, 'challenges'),
    acceptTermsAndConditions: buildPath(SCOPES.ambassadors, 'accept_t_and_c'),
    jobPositionPerformanceMetrics: buildPath(SCOPES.ambassadors, 'job_position_performance_metrics'),
    managed: buildPath(SCOPES.ambassadors, 'managed'),
    managedAggregate: buildPath(SCOPES.ambassadors, 'managed', 'aggregate'),
    managedRoster: buildPath(SCOPES.ambassadors, 'managed', 'roster'),
    managedMetrics: (code: string) => buildPath(SCOPES.ambassadors, 'managed', code, 'metrics'),
    managedJobPositionPerformanceMetrics: (code: string) =>
      buildPath(SCOPES.ambassadors, 'managed', code, 'job_position_performance_metrics'),
  },
  employers: {
    root: SCOPES.employers,
    graphql: buildPath(SCOPES.graphql, SCOPES.employers),
    picture: buildPath(SCOPES.employers, 'picture'),
    invoices: buildPath(SCOPES.employers, 'invoices'),
    notes: buildPath(SCOPES.employers, 'notes'),
    subscription: buildPath(SCOPES.employers, 'subscription'),
    paymentMethod: buildPath(SCOPES.employers, 'payment_method'),
    assignCustomerSuccessManager: buildPath(SCOPES.employers, 'assign_customer_success_manager'),
    employerContact: buildPath(SCOPES.employers, 'contact'),
    stripeCustomerPortal: buildPath(SCOPES.employers, 'stripe_customer_portal'),
    subscriptionSession: buildPath(SCOPES.employers, 'subscription_session'),
    createCheckoutSession: buildPath(SCOPES.employers, 'create_checkout_session'),
    invoicePdf: buildPath(SCOPES.employers, 'invoice', 'pdf'),
    accessLink: buildPath(SCOPES.employers, 'access_link'),
    subscriptionFeedback: buildPath(SCOPES.employers, 'subscription_feedback'),
    searchCompanies: buildPath(SCOPES.employers, 'search_companies'),
    subscriptionEmail: buildPath(SCOPES.employers, 'subscription', 'email'),
    followups: buildPath(SCOPES.employers, 'followups'),
    employerContacts: buildPath(SCOPES.employers, 'employer_contacts'),
    adPerformanceStatistics: buildPath(SCOPES.employers, '/job_positions/statistics/get_ad_performance'),
    applicationPerformance: buildPath(SCOPES.employers, '/job_positions/application_performance'),
    isSubscriptionFeedbacksSubmitted: buildPath(SCOPES.employers, 'is_feedback_submitted_recently'),
    cancelStripeSubscription: buildPath(SCOPES.employers, 'cancel_stripe_subscription'),
    fetchEmployerSubscriptionFeedbackByOrganization: buildPath(
      SCOPES.employers,
      'fetch_employer_subscription_feedback'
    ),
    referralLink: buildPath(SCOPES.employers, 'referral_link'),
    // Self-serve 1990 credit packs (RW-397)
    creditsPackages: buildPath(SCOPES.employers, 'credits', 'packages'),
    creditsQuote: buildPath(SCOPES.employers, 'credits', 'quote'),
    creditsPurchase: buildPath(SCOPES.employers, 'credits', 'purchase'),
    creditsCustomRequest: buildPath(SCOPES.employers, 'credits', 'custom-request'),
  },
  restaurants: {
    root: SCOPES.restaurants,
    localities: buildPath(SCOPES.restaurants, 'locality'),
    picture: buildPath(SCOPES.restaurants, 'picture'),
    gMapPlaces: buildPath(SCOPES.restaurants, 'places_info'),
    popularLocalities: buildPath(SCOPES.employers, 'popular', 'localities'),
    restaurantIds: buildPath(SCOPES.restaurants, 'ids'),
    restaurantLocationCoordinates: buildPath(SCOPES.restaurants, 'explore', 'map'),
    members: buildPath(SCOPES.restaurants, 'employees'),
    employees: buildPath(SCOPES.restaurants, 'employees'),
    occupationTemplates: buildPath(SCOPES.restaurants, 'occupation_templates'),
    violaBadge: buildPath(SCOPES.restaurants, 'show_viola_badge'),
    referentContact: buildPath(SCOPES.restaurants, 'referent_contact'),
    restaurantNames: buildPath(SCOPES.restaurants, 'names'),
    statistics: buildPath(SCOPES.restaurants, 'statistics'),
    restaurantViewLog: buildPath(SCOPES.restaurants, 'log_view'),
    publicRestaurant: buildPath(SCOPES.restaurants, 'public'),
    additionalOnboardingData: buildPath(SCOPES.restaurants, 'additional_information'),
    checkDuplicate: buildPath(SCOPES.restaurants, 'check_duplicate'),
    otherRestaurantValues: buildPath(SCOPES.restaurants, 'other_restaurant_values'),
    checkExistence: buildPath(SCOPES.restaurants, 'check_existence'),
  },
  jobPositions: {
    root: SCOPES.job_positions,
    status: buildPath(SCOPES.job_positions, 'status'),
    active_status: buildPath(SCOPES.job_positions, 'active_status'),
    assignCsm: buildPath(SCOPES.job_positions, 'assign_customer_success_manager'),
    makeCsmMain: buildPath(SCOPES.job_positions, 'set_primary_customer_success_manager'),
    removeCsm: buildPath(SCOPES.job_positions, 'remove_customer_success_manager'),
    duplicate: buildPath(SCOPES.job_positions, 'duplicate'),
    stats: buildPath(SCOPES.job_positions, 'stats'),
    task: buildPath(SCOPES.job_positions, 'task'),
    applicationPerformanceMetrics: buildPath(SCOPES.job_positions, 'application_performance'),
    jobPositionActive: buildPath(SCOPES.job_positions, 'active'),
    JPWorker: buildPath(SCOPES.job_positions, 'workers'),
    jobPositionViewLog: buildPath(SCOPES.job_positions, 'log_view'),
    referralJobPositions: buildPath(SCOPES.employers, 'referral_job_positions'),
    similarJobs: buildPath(SCOPES.job_positions, 'similar'),
    favoriteJob: buildPath(SCOPES.job_positions, 'favorite'),
    isJobFavorite: buildPath(SCOPES.job_positions, 'favorited'),
    showGenerationPrompt: buildPath(SCOPES.job_positions, 'show_generation_prompt'),
    chatCompletion: buildPath(SCOPES.job_positions, 'chat_completion'),
    generateAdContent: buildPath(SCOPES.job_positions, 'generate_ad_content'),
    /** @deprecated Use generateAdContent instead */
    createJobTitle: buildPath(SCOPES.job_positions, 'create_job_title'),
    /** @deprecated Use generateAdContent instead */
    createJobDescription: buildPath(SCOPES.job_positions, 'create_job_description'),
    /** @deprecated Use generateAdContent instead */
    createJobContractDescription: buildPath(SCOPES.job_positions, 'create_job_contract_description'),
    regions: buildPath(SCOPES.job_positions, 'regions'),
    JobLocationCoordinates: buildPath(SCOPES.job_positions, 'explore/map'),
    JobLocationPositionList: buildPath(SCOPES.job_positions, 'explore'),
    groupedCount: buildPath(SCOPES.job_positions, 'grouped_count'),
    assignJR: buildPath(SCOPES.job_positions, 'assign_junior_recruiter'),
    requestAssistance: buildPath(SCOPES.job_positions, 'request_assistance'),
    assessmentQuestions: buildPath(SCOPES.job_positions, 'assessment_questions'),
  },
  workers: {
    root: SCOPES.workers,
    graphql: buildPath(SCOPES.graphql, SCOPES.workers),
    me: buildPath(SCOPES.workers, 'me'),
    boostStatus: buildPath(SCOPES.workers, 'subscription_status'),
    researchStatus: buildPath(SCOPES.workers, 'check_research_status'),
    applicationStatus: buildPath(SCOPES.workers, 'check_application_status'),
    asset: buildPath(SCOPES.workers, 'asset'),
    minifyImage: buildPath(SCOPES.workers, 'minify_image'),
    nativeCV: buildPath(SCOPES.workers, 'native_cv'),
    emailValidity: buildPath(SCOPES.workers, 'check_validity'),
    sendPicture: buildPath(SCOPES.workers, 'picture'),
    updateResearchStatus: buildPath(SCOPES.workers, 'update_research_status'),
    activeApplications: buildPath(SCOPES.workers, 'active_applications'),
    sendEmail: buildPath(SCOPES.workers, 'send_email'),
    sendAccessEmail: buildPath(SCOPES.workers, 'send_access_email'),
    uploadCv: buildPath(SCOPES.workers, 'upload_cv'),
    uploadCv_2: buildPath(SCOPES.workers, 'cv'),
    deleteCv: buildPath(SCOPES.workers, 'delete_cv'),
    notes: buildPath(SCOPES.workers, 'notes'),
    createWorker: buildPath(SCOPES.workers, 'worker'),
    savedResearch: buildPath(SCOPES.workers, 'saved_research'),
    savedResearches: buildPath(SCOPES.workers, 'saved_researches'),
    similarWorkers: buildPath(SCOPES.workers, 'similar'),
    workerPastExperiences: buildPath(SCOPES.workers, 'past_experiences'),
    workerPastExperienceInconsistencies: buildPath(SCOPES.workers, 'past_experiences', 'inconsistencies'),
    references: buildPath(SCOPES.workers, 'references'),
    cvParsingJobStatus: buildPath(SCOPES.workers, 'cv_parsing_job_status'),
    referralProgram: buildPath(SCOPES.workers, 'referral_program'),
    referralProgramEnroll: buildPath(SCOPES.workers, 'referral_program', 'enroll'),
    referralProgramStats: buildPath(SCOPES.workers, 'referral_program', 'stats'),
  },
  workerJobPositions: {
    root: SCOPES.worker_job_positions,
    graphql: buildPath(SCOPES.graphql, SCOPES.worker_job_positions),
    jobPosition: buildPath(SCOPES.worker_job_positions, 'job_positions'),
    jobApplication: buildPath(SCOPES.worker_job_positions, 'job_application'),
    shortlist: buildPath(SCOPES.worker_job_positions, 'shortlist'),
    addWorkerToShortlist: buildPath(SCOPES.worker_job_positions, 'add_worker_to_shortlist'),
    removeWorkerFromShortlist: buildPath(SCOPES.worker_job_positions, 'remove_worker_from_shortlist'),
    workerStatus: buildPath(SCOPES.worker_job_positions, 'status'),
    updateStatus: buildPath(SCOPES.worker_job_positions, 'shortlist', 'status'),
    shortlists: buildPath(SCOPES.worker_job_positions, 'shortlists'),
    sendShortlist: buildPath(SCOPES.worker_job_positions, 'shortlist', 'send'),
    cleanPendingApplications: buildPath(SCOPES.worker_job_positions, 'job_applications', 'clean_pending'),
    updateWorkerShortlist: buildPath(SCOPES.worker_job_positions, 'worker_shortlist', 'add_note'),
    getCompatibilityScore: buildPath(SCOPES.worker_job_positions, 'affinity_score'),
    workerApply: buildPath(SCOPES.worker_job_positions, 'apply'),
    jobApplyStatus: buildPath(SCOPES.worker_job_positions, 'job_application'),
    listSpontaneousApplications: buildPath(SCOPES.worker_job_positions, 'spontaneous_applications'),
    spontaneousApplication: buildPath(SCOPES.worker_job_positions, 'spontaneous_application'),
    jobApplicationsHistory: buildPath(SCOPES.worker_job_positions, 'job_applications', 'history'),
    jobApplications: buildPath(SCOPES.worker_job_positions, 'job_applications'),
    conversationSteps: buildPath(SCOPES.worker_job_positions, 'job_applications', 'conversations', 'steps'),
    workerPresentations: buildPath(SCOPES.worker_job_positions, 'worker_presentations'),
    jobApplicationComplaints: buildPath(SCOPES.worker_job_positions, 'job_applications', 'complaints'),
    candidateEvaluations: buildPath(SCOPES.worker_job_positions, 'job_applications', 'evaluations'),
    discardReasons: buildPath(SCOPES.worker_job_positions, 'job_applications'),
    /*
     * Three sibling names that autocomplete cannot tell apart, so each says what it is for.
     * The paths are right; the risk is picking the wrong one by accident (#397 review).
     */
    /** The worker's own chat, read and written: `…/assessments/:job_position_id`. */
    assessments: buildPath(SCOPES.worker_job_positions, 'assessments'),
    /** Operations on the *worker* of an assessment, not on the conversation. */
    assessmentsWorker: buildPath(SCOPES.worker_job_positions, 'assessments', 'worker'),
    /** The conversation behind an assessment, for internal callers rather than the worker. */
    assessmentsConversation: buildPath(SCOPES.worker_job_positions, 'assessments', 'conversation'),
    sendManualAssessmentMessage: buildPath(SCOPES.worker_job_positions, 'assessments', 'conversation'),
  },
  dashboard: {
    root: SCOPES.dashboard,
    rwMemberStats: buildPath(SCOPES.dashboard, 'rw_members', 'stats'),
    historyActiveJobPositions: buildPath(SCOPES.dashboard, 'rw_members', 'average_active_job_positions'),
    getAssistanceSatisfactionStatistics: buildPath(
      SCOPES.dashboard,
      'rw_members',
      'get_assistance_satisfaction_statistics'
    ),
    csmWinRate: buildPath(SCOPES.dashboard, 'rw_members', 'csm_win_rate'),
    averageShortlistsAndWorkersSent: buildPath(SCOPES.dashboard, 'rw_members', 'average_shortlists_and_workers_sent'),
    jobPositionAverageClosingTime: buildPath(SCOPES.dashboard, 'rw_members', 'job_position_average_closing_time'),
  },
  taxonomy: {
    root: SCOPES.taxonomy,
    skill: buildPath(SCOPES.taxonomy, 'skill'),
    occupation: buildPath(SCOPES.taxonomy, 'occupation'),
    occupationSkills: buildPath(SCOPES.taxonomy, 'occupation_skills'),
    popularOccupations: buildPath(SCOPES.taxonomy, 'popular', 'occupations'),
  },
  cvParsing: {
    root: SCOPES.cv_parsing,
    unanalysedParsedCv: buildPath(SCOPES.cv_parsing, 'unanalysed_parsed_cv'),
    collectErrors: buildPath(SCOPES.cv_parsing, 'collect_errors'),
    setCvAsAnalysed: buildPath(SCOPES.cv_parsing, 'set_cv_as_analysed'),
    correctedCvCount: buildPath(SCOPES.cv_parsing, 'corrected_cv_count'),
  },
  socialAuth: {
    root: SCOPES.social_auth,
    google: buildPath(SCOPES.social_auth, 'google'),
    facebook: buildPath(SCOPES.social_auth, 'facebook'),
  },
  organizations: {
    root: SCOPES.organizations,
    twoStep: buildPath(SCOPES.organizations, 'two_step'),
    paymentMethods: buildPath(SCOPES.organizations, 'payment_methods'),
    paymentMethodSetupIntent: buildPath(SCOPES.organizations, 'payment_methods', 'setup_intent'),
    updateStripeCustomer: buildPath(SCOPES.organizations, 'stripe_customer'),
    followups: buildPath(SCOPES.organizations, 'followups'),
    missedOrgApplicationActivities: buildPath(SCOPES.organizations, 'get_missed_org_application_activities'),
    flexibleSlotPrices: buildPath(SCOPES.organizations, 'get_flexible_slot_prices'),
    discrepancies: buildPath(SCOPES.organizations, 'discrepancies'),
    performanceMetrics: buildPath(SCOPES.organizations, 'performance_metrics'),
    structuredNotes: buildPath(SCOPES.organizations, 'structured_notes'),
    snooze: buildPath(SCOPES.organizations, 'snooze'),
    checkInvoicesStatus: buildPath(SCOPES.organizations, 'check_invoices_status'),
    customerDiscoveryComplete: buildPath(SCOPES.organizations, 'customer_discovery', 'complete'),
    customerDiscoveryBooked: buildPath(SCOPES.organizations, 'customer_discovery', 'booked'),
    referralBonusInfo: buildPath(SCOPES.organizations, 'referral', 'bonus_info'),
    referralRequests: buildPath(SCOPES.organizations, 'referral_requests'),
    checkReferralRequestExistence: buildPath(SCOPES.organizations, 'referral_requests', 'check_existence'),
    prospects: buildPath(SCOPES.organizations, 'prospects'),
  },
  companies: {
    root: SCOPES.companies,
    subscriptions: buildPath(SCOPES.companies, 'subscriptions'),
    subscriptionEmail: buildPath(SCOPES.companies, 'subscription', 'email'),
    stripeCustomerPortal: buildPath(SCOPES.companies, 'stripe_customer_portal'),
  },
  klaaryo: {
    root: SCOPES.klaaryo,
  },
  surveys: {
    root: SCOPES.surveys,
    pending: buildPath(SCOPES.surveys, 'pending'),
    response: (surveyKey: string) => buildPath(SCOPES.surveys, surveyKey, 'response'),
  },
};
